// src/pages/BlogPage.js

import React, { useState, useEffect, useContext } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ThemeContext } from '../context/ThemeContext';
import { articleService, articleCategoryService, tagService } from '../api';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import useApi from '../hooks/useApi';

// Styled components
const BlogSection = styled.section`
  padding: 3rem 2rem;
  background: ${props => props.theme.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BlogHeader = styled.div`
  margin-bottom: 2.5rem;
  text-align: center;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 2.5rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ArticlesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.aside`
  @media (max-width: 992px) {
    order: -1; // Place sidebar above articles on mobile
  }
`;

const SidebarSection = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 12px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const SidebarTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1.2rem;
  color: ${props => props.theme.primary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -8px;
    width: 50px;
    height: 3px;
    background-color: ${props => props.theme.accent};
  }
`;

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CategoryItem = styled.li`
  margin-bottom: 0.8rem;
  
  a {
    display: flex;
    justify-content: space-between;
    color: ${props => props.theme.text};
    text-decoration: none;
    padding: 0.6rem 0;
    transition: all 0.2s ease;
    
    &:hover, &.active {
      color: ${props => props.theme.primary};
      transform: translateX(5px);
    }
    
    .count {
      background: ${props => props.theme.backgroundSecondary};
      padding: 0.2rem 0.8rem;
      border-radius: 20px;
      font-size: 0.85rem;
      font-weight: 500;
    }
  }
`;

const TagsCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
`;

const TagItem = styled(Link)`
  display: inline-block;
  background: ${props => props.active ? props.theme.primary : props.theme.backgroundSecondary};
  color: ${props => props.active ? 'white' : props.theme.text};
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.primary + '30'};
    transform: translateY(-2px);
  }
`;

const SearchBox = styled.div`
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.9rem 1.2rem;
  border: 2px solid ${props => props.theme.text}20;
  border-radius: 8px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary}80;
    box-shadow: 0 0 0 3px ${props => props.theme.primary}20;
  }
`;

const ArticleCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 12px;
  box-shadow: ${props => props.theme.card.shadow};
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.card.hover};
  }
`;

const ArticleImage = styled.div`
  width: 100%;
  height: 220px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(to top, rgba(0,0,0,0.5), transparent);
  }
`;

const ArticleContent = styled.div`
  padding: 1.8rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ArticleTitle = styled.h3`
  font-size: 1.4rem;
  line-height: 1.3;
  margin-bottom: 0.8rem;
  color: ${props => props.theme.text};
  
  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${props => props.theme.primary};
    }
  }
`;

const ArticleMeta = styled.div`
  display: flex;
  gap: 1.2rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: ${props => props.theme.text}99;
  
  span {
    display: flex;
    align-items: center;
    
    i {
      margin-right: 0.5rem;
    }
  }
`;

const ArticleExcerpt = styled.p`
  color: ${props => props.theme.text}DD;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  flex: 1;
`;

const ArticleStats = styled.div`
  display: flex;
  gap: 1.2rem;
  margin-bottom: 1.2rem;
  font-size: 0.9rem;
  color: ${props => props.theme.text}BB;
  
  span {
    display: flex;
    align-items: center;
    
    i {
      margin-right: 0.5rem;
      color: ${props => props.theme.primary};
    }
  }
`;

const ReadMoreButton = styled(Button)`
  align-self: flex-start;
  margin-top: auto;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateX(3px);
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3.5rem;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  width: 42px;
  height: 42px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: 2px solid ${props => props.active ? props.theme.primary : props.theme.text + '20'};
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.text};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.active ? props.theme.primary : props.theme.backgroundSecondary};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.text};
  background: ${props => props.theme.card.background};
  border-radius: 12px;
  box-shadow: ${props => props.theme.card.shadow};
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
`;

const BlogPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const { categorySlug, tagSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // États
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [articles, setArticles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  
  // Paramètres de l'API
  const articlesParams = {
    page: currentPage,
    is_published: true
  };
  
  // Appliquer les filtres selon les paramètres d'URL
  if (categorySlug) articlesParams.category__slug = categorySlug;
  if (tagSlug) articlesParams.tags__slug = tagSlug;
  if (searchTerm) articlesParams.search = searchTerm;
  
  // Charger les articles
  const { 
    data: articlesData, 
    loading: articlesLoading, 
    error: articlesError,
    refetch: refetchArticles
  } = useApi(() => articleService.getAll(articlesParams), [currentPage, categorySlug, tagSlug, searchTerm]);
  
  // Traiter les données des articles
  useEffect(() => {
    if (articlesData) {
      console.log('Articles data received:', articlesData);
      
      // Gérer différentes structures possibles de réponse API
      if (Array.isArray(articlesData)) {
        // Si c'est un tableau direct d'articles
        setArticles(articlesData);
        setTotalCount(articlesData.length);
      } else if (articlesData.results && Array.isArray(articlesData.results)) {
        // Si c'est une structure paginée avec results
        setArticles(articlesData.results);
        setTotalCount(articlesData.count || articlesData.results.length);
      } else if (articlesData.id) {
        // Si c'est un seul article
        setArticles([articlesData]);
        setTotalCount(1);
      } else {
        // Si la structure est différente ou vide
        setArticles([]);
        setTotalCount(0);
      }
    }
  }, [articlesData]);
  
  // Charger les catégories
  const { 
    data: categoriesData, 
    loading: categoriesLoading, 
    error: categoriesError 
  } = useApi(() => articleCategoryService.getAll(), []);
  
  // Charger les tags
  const { 
    data: tagsData, 
    loading: tagsLoading, 
    error: tagsError 
  } = useApi(() => tagService.getAll(), []);

  // Traitement des catégories
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    if (categoriesData) {
      if (Array.isArray(categoriesData)) {
        setCategories(categoriesData);
      } else if (categoriesData.results && Array.isArray(categoriesData.results)) {
        setCategories(categoriesData.results);
      } else {
        setCategories([]);
      }
    }
  }, [categoriesData]);

  // Traitement des tags
  const [tags, setTags] = useState([]);
  useEffect(() => {
    if (tagsData) {
      if (Array.isArray(tagsData)) {
        setTags(tagsData);
      } else if (tagsData.results && Array.isArray(tagsData.results)) {
        setTags(tagsData.results);
      } else {
        setTags([]);
      }
    }
  }, [tagsData]);
  
  // Animation au scroll
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  // Gestion de la recherche
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchParams({ search: searchTerm });
  };
  
  // Pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Formatage de la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(navigator.language, options);
  };
  
  // Total des pages
  const totalPages = Math.max(1, Math.ceil(totalCount / 10));
  
  // Titre de la page selon les filtres
  let pageTitle = t('blog', 'Blog');
  let pageSubtitle = t('blogSubtitle', 'Découvrez nos derniers articles');
  
  if (categorySlug && categories.length > 0) {
    const category = categories.find(c => c.slug === categorySlug);
    if (category) {
      pageTitle = t('categoryArticles', 'Articles dans {{category}}', { category: category.name });
      pageSubtitle = category.description || t('blogSubtitle', 'Découvrez nos derniers articles');
    }
  } else if (tagSlug && tags.length > 0) {
    const tag = tags.find(t => t.slug === tagSlug);
    if (tag) {
      pageTitle = t('tagArticles', 'Articles avec le tag {{tag}}', { tag: tag.name });
      pageSubtitle = t('blogSubtitle', 'Découvrez nos derniers articles');
    }
  } else if (searchTerm) {
    pageTitle = t('searchResults', 'Résultats de recherche');
    pageSubtitle = t('searchResultsFor', 'Pour "{{term}}"', { term: searchTerm });
  }

  // Stats des articles
  const getArticleStats = (articleId) => {
    const likes = localStorage.getItem(`article_${articleId}_likes`) || 0;
    const commentsStr = localStorage.getItem(`article_${articleId}_comments`);
    const comments = commentsStr ? JSON.parse(commentsStr).length : 0;
    return { likes, comments };
  };
  
  return (
    <BlogSection theme={theme}>
      <Container>
        <BlogHeader>
          <SectionTitle
            title={pageTitle}
            subtitle={pageSubtitle}
          />
        </BlogHeader>
        
        <BlogGrid>
          <div>
            {articlesLoading ? (
              <LoadingIndicator>
                <svg width="60" height="60" viewBox="0 0 50 50">
                  <circle 
                    cx="25" 
                    cy="25" 
                    r="20" 
                    fill="none" 
                    stroke={theme.primary} 
                    strokeWidth="4" 
                    strokeLinecap="round"
                  >
                    <animateTransform 
                      attributeName="transform" 
                      type="rotate" 
                      from="0 25 25" 
                      to="360 25 25" 
                      dur="1.5s" 
                      repeatCount="indefinite"
                    />
                    <animate 
                      attributeName="stroke-dasharray" 
                      from="1, 150" 
                      to="90, 150" 
                      dur="1.5s" 
                      repeatCount="indefinite"
                    />
                    <animate 
                      attributeName="stroke-dashoffset" 
                      from="0" 
                      to="-35" 
                      dur="1.5s" 
                      repeatCount="indefinite"
                    />
                  </circle>
                </svg>
              </LoadingIndicator>
            ) : articlesError ? (
              <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div style={{ fontSize: '4rem', color: theme.error, marginBottom: '1rem' }}>
                    <i className="fas fa-exclamation-circle"></i>
                  </div>
                  <h2 style={{ fontSize: '1.8rem', color: theme.text, marginBottom: '1.5rem' }}>
                    {typeof articlesError === 'string' ? articlesError : t('errorLoadingArticles', "Erreur lors du chargement des articles")}
                  </h2>
                  <Button variant="primary" onClick={() => refetchArticles()}>
                    <i className="fas fa-redo"></i> {t('tryAgain', "Réessayer")}
                  </Button>
                </motion.div>
              </div>
            ) : articles.length === 0 ? (
              <NoResults theme={theme}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                    <i className="fas fa-search"></i>
                  </div>
                  <h3>{t('noArticlesFound', "Aucun article trouvé")}</h3>
                  <p>{t('tryDifferentSearch', "Essayez une recherche différente ou consultez toutes les catégories")}</p>
                </motion.div>
              </NoResults>
            ) : (
              <>
                <ArticlesGrid ref={ref}>
                  {articles.map((article, index) => {
                    const stats = getArticleStats(article.id);
                    
                    return (
                      <motion.div
                        key={article.id}
                        initial={{ opacity: 0, y: 30 }}
                        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <ArticleCard theme={theme}>
                          {article.featured_image && (
                            <ArticleImage src={article.featured_image} />
                          )}
                          
                          <ArticleContent>
                            <ArticleTitle theme={theme}>
                              <Link to={`/blog/${article.id}`}>{article.title}</Link>
                            </ArticleTitle>
                            
                            <ArticleMeta theme={theme}>
                              <span>
                                <i className="fas fa-user"></i>
                                {article.author ? (article.author.first_name || article.author.username) : 'Admin'}
                              </span>
                              <span>
                                <i className="fas fa-calendar-alt"></i>
                                {formatDate(article.published_at || article.created_at)}
                              </span>
                            </ArticleMeta>
                            
                            <ArticleStats theme={theme}>
                              <span>
                                <i className="fas fa-heart"></i>
                                {stats.likes} {t('likes', "J'aime")}
                              </span>
                              <span>
                                <i className="fas fa-comment"></i>
                                {stats.comments} {t('comments', "Commentaires")}
                              </span>
                            </ArticleStats>
                            
                            <ArticleExcerpt theme={theme}>
                              {article.excerpt || (article.content && article.content.substring(0, 150) + '...') || ''}
                            </ArticleExcerpt>
                            
                            <ReadMoreButton
                              variant="outline"
                              size="small"
                              to={`/blog/${article.id}`}
                            >
                              {t('readMore', "Lire la suite")} <i className="fas fa-arrow-right" style={{ marginLeft: '0.5rem' }}></i>
                            </ReadMoreButton>
                          </ArticleContent>
                        </ArticleCard>
                      </motion.div>
                    );
                  })}
                </ArticlesGrid>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <PaginationContainer>
                    <PaginationButton
                      theme={theme}
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </PaginationButton>
                    
                    <PaginationButton
                      theme={theme}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-angle-left"></i>
                    </PaginationButton>
                    
                    {Array.from({ length: totalPages }).map((_, index) => {
                      // Afficher les 2 pages avant et après la page actuelle, ainsi que la première et la dernière
                      if (
                        index === 0 ||
                        index === totalPages - 1 ||
                        (index >= currentPage - 2 && index <= currentPage + 1)
                      ) {
                        return (
                          <PaginationButton
                            key={index}
                            theme={theme}
                            active={currentPage === index + 1}
                            onClick={() => handlePageChange(index + 1)}
                          >
                            {index + 1}
                          </PaginationButton>
                        );
                      } else if (
                        index === currentPage - 3 ||
                        index === currentPage + 2
                      ) {
                        return <span key={index} style={{ alignSelf: 'center' }}>...</span>;
                      }
                      return null;
                    })}
                    
                    <PaginationButton
                      theme={theme}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <i className="fas fa-angle-right"></i>
                    </PaginationButton>
                    
                    <PaginationButton
                      theme={theme}
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </PaginationButton>
                  </PaginationContainer>
                )}
              </>
            )}
          </div>
          
          <Sidebar>
            {/* Recherche */}
            <SidebarSection theme={theme}>
              <SidebarTitle theme={theme}>{t('search', "Recherche")}</SidebarTitle>
              <SearchBox>
                <form onSubmit={handleSearch}>
                  <SearchInput
                    type="text"
                    placeholder={t('searchPlaceholder', "Rechercher des articles...")}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    theme={theme}
                  />
                </form>
              </SearchBox>
            </SidebarSection>
            
            {/* Catégories */}
            <SidebarSection theme={theme}>
              <SidebarTitle theme={theme}>{t('categories', "Catégories")}</SidebarTitle>
              {categoriesLoading ? (
                <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                  <i className="fas fa-spinner fa-spin"></i>
                </div>
              ) : categoriesError ? (
                <div style={{ color: theme.error }}>{t('errorLoadingCategories', "Erreur lors du chargement des catégories")}</div>
              ) : categories.length === 0 ? (
                <div>{t('noCategories', "Aucune catégorie disponible")}</div>
              ) : (
                <CategoryList>
                  <CategoryItem>
                    <Link 
                      to="/blog" 
                      className={!categorySlug && !tagSlug && !searchTerm ? 'active' : ''}
                      theme={theme}
                    >
                      <span>{t('allCategories', "Toutes les catégories")}</span>
                      <span className="count">{totalCount}</span>
                    </Link>
                  </CategoryItem>
                  {categories.map(category => (
                    <CategoryItem key={category.id}>
                      <Link 
                        to={`/blog/category/${category.slug}`} 
                        className={categorySlug === category.slug ? 'active' : ''}
                        theme={theme}
                      >
                        <span>{category.name}</span>
                        <span className="count">{category.articles_count || 0}</span>
                      </Link>
                    </CategoryItem>
                  ))}
                </CategoryList>
              )}
            </SidebarSection>
            
            {/* Tags */}
            <SidebarSection theme={theme}>
              <SidebarTitle theme={theme}>{t('tags', "Tags")}</SidebarTitle>
              {tagsLoading ? (
                <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                  <i className="fas fa-spinner fa-spin"></i>
                </div>
              ) : tagsError ? (
                <div style={{ color: theme.error }}>{t('errorLoadingTags', "Erreur lors du chargement des tags")}</div>
              ) : tags.length === 0 ? (
                <div>{t('noTags', "Aucun tag disponible")}</div>
              ) : (
                <TagsCloud>
                  {tags.map(tag => (
                    <TagItem 
                      key={tag.id} 
                      to={`/blog/tag/${tag.slug}`}
                      active={tagSlug === tag.slug}
                      theme={theme}
                    >
                      {tag.name}
                    </TagItem>
                  ))}
                </TagsCloud>
              )}
            </SidebarSection>
          </Sidebar>
        </BlogGrid>
      </Container>
    </BlogSection>
  );
};

export default BlogPage;